/**
 * Geometry for the undocked ("floating") chat panel.
 *
 * Kept as pure rect math — no DOM, no signals — so dragging, resizing and
 * viewport clamping are testable and the component only has to translate
 * pointer events into deltas.
 */

export interface FloatRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/** Breathing room left under the panel when it lifts off the bottom edge. */
export const UNDOCK_BOTTOM_GAP = 10;

/**
 * How far the header has to travel before a docked panel tears off. Below this
 * the gesture is still a click on the header, not an undock.
 */
export const TEAR_OFF_DISTANCE = 6;

/** Floor for user resizing — below this the composer and header stop fitting. */
export const MIN_FLOAT_WIDTH = 320;
export const MIN_FLOAT_HEIGHT = 240;

/** Screen edge an edge-docked panel is pinned to. */
export type DockEdge = "left" | "right" | "top" | "bottom";

/** Share of the viewport an edge dock takes on the edge's own axis. */
export const EDGE_DOCK_RATIO = 1 / 3;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Keep the panel inside the viewport and no smaller than the usable minimum. */
export function clampFloatRect(rect: FloatRect, viewport: Viewport): FloatRect {
  const width = clamp(rect.width, MIN_FLOAT_WIDTH, Math.max(MIN_FLOAT_WIDTH, viewport.width));
  const height = clamp(
    rect.height,
    MIN_FLOAT_HEIGHT,
    Math.max(MIN_FLOAT_HEIGHT, viewport.height),
  );
  return {
    width,
    height,
    left: clamp(rect.left, 0, Math.max(0, viewport.width - width)),
    top: clamp(rect.top, 0, Math.max(0, viewport.height - height)),
  };
}

/** True once a header drag has moved far enough to count as pulling off dock. */
export function exceedsTearOff(deltaX: number, deltaY: number): boolean {
  return Math.hypot(deltaX, deltaY) >= TEAR_OFF_DISTANCE;
}

/** Fallback placement when the docked box can't be measured (no element yet). */
export function defaultFloatRect(viewport: Viewport): FloatRect {
  const width = Math.min(640, viewport.width);
  const height = Math.min(600, viewport.height);
  return clampFloatRect(
    {
      width,
      height,
      left: viewport.width - width - 24,
      top: viewport.height - height - UNDOCK_BOTTOM_GAP,
    },
    viewport,
  );
}

/**
 * The edge of a docked panel that faces the page — the only one the user can
 * pull on, since the other three sit against the screen.
 */
export function innerEdge(dock: DockEdge): DockEdge {
  switch (dock) {
    case "left":
      return "right";
    case "right":
      return "left";
    case "top":
      return "bottom";
    case "bottom":
      return "top";
  }
}

/**
 * Edge dock: a third of the viewport on the edge's own axis, the full span on
 * the other, flush against that edge. Clamping still applies, so on a viewport
 * too narrow/short for a third the panel grows to the usable minimum and stays
 * pinned. The share is rounded to whole pixels — a third rarely divides evenly,
 * and a subpixel box renders the panel's border as a blurred hairline.
 *
 * `span` overrides the third once the user has dragged the inner edge, so the
 * width they chose survives a window resize instead of snapping back.
 */
export function edgeDockRect(
  edge: DockEdge,
  viewport: Viewport,
  span?: number,
): FloatRect {
  const horizontal = edge === "left" || edge === "right";
  const axis = span ?? Math.round((horizontal ? viewport.width : viewport.height) * EDGE_DOCK_RATIO);
  const width = horizontal ? axis : viewport.width;
  const height = horizontal ? viewport.height : axis;
  return clampFloatRect(
    {
      width,
      height,
      left: edge === "right" ? viewport.width - width : 0,
      top: edge === "bottom" ? viewport.height - height : 0,
    },
    viewport,
  );
}

/** Header drag: move the panel by a pointer delta, staying on screen. */
export function movedRect(
  start: FloatRect,
  deltaX: number,
  deltaY: number,
  viewport: Viewport,
): FloatRect {
  return clampFloatRect(
    { ...start, left: start.left + deltaX, top: start.top + deltaY },
    viewport,
  );
}

/**
 * Edge drag: pull one side of the panel, leaving the other three where they
 * are. Dragging the left or top side moves that side's coordinate as well as
 * the size, which is what keeps a right- or bottom-docked panel flush against
 * its screen edge while it grows inward.
 */
export function resizedRect(
  start: FloatRect,
  edge: DockEdge,
  deltaX: number,
  deltaY: number,
  viewport: Viewport,
): FloatRect {
  switch (edge) {
    case "right": {
      const room = Math.max(MIN_FLOAT_WIDTH, viewport.width - start.left);
      return { ...start, width: clamp(start.width + deltaX, MIN_FLOAT_WIDTH, room) };
    }
    case "left": {
      const right = start.left + start.width;
      const width = clamp(start.width - deltaX, MIN_FLOAT_WIDTH, Math.max(MIN_FLOAT_WIDTH, right));
      return { ...start, width, left: right - width };
    }
    case "bottom": {
      const room = Math.max(MIN_FLOAT_HEIGHT, viewport.height - start.top);
      return { ...start, height: clamp(start.height + deltaY, MIN_FLOAT_HEIGHT, room) };
    }
    case "top": {
      const bottom = start.top + start.height;
      const height = clamp(
        start.height - deltaY,
        MIN_FLOAT_HEIGHT,
        Math.max(MIN_FLOAT_HEIGHT, bottom),
      );
      return { ...start, height, top: bottom - height };
    }
  }
}

/**
 * Grow the panel by `extra` pixels for content that opens inside it (the
 * suggestion tray) without shrinking the conversation. The stored rect is left
 * alone so closing that content restores the exact box. Growth goes upward
 * first — the panel usually sits near the bottom — and spills downward only
 * when the top of the screen is reached; it never exceeds the viewport.
 */
export function expandedRect(rect: FloatRect, extra: number, viewport: Viewport): FloatRect {
  if (extra <= 0) return rect;
  const height = Math.min(rect.height + extra, viewport.height);
  const grown = height - rect.height;
  const top = Math.max(0, rect.top - grown);
  return {
    ...rect,
    top,
    height: Math.min(height, viewport.height - top),
  };
}
